/* Imports needed for all question form subclasses. */
import * as React from 'react';
import * as classnames from 'classnames';
import { withRouter } from 'react-router';
import {
    QuestionFormBase,
    QuestionFormBaseProps,
    QuestionFormBaseState,
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { MULTIMEDIA_TYPES, QTYPES, TENANT_LEVEL } from '@nexxt/common/types';
import NotifyHOC from '../../../../../../HOC/Notifications';
import Select from 'react-select';
/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import QuestionSettings from '../../../common/questionSettings';
import Toggle from '../../../../../common/Toggle';
import Notes from '../../../../../common/notes/Notes';
import { parseQuestionSummaryToObjects } from '../../../../../../utils/question';
import ImageModal from '../../../common/ImageModal';
import MultimediaBubble from '../../../common/MultimediaBubble';
import IndicatorsContainer from '../../../../../common/reactSelect/IndicatorsContainer';
import MediaLibraryAddModal from '../../../../../libraries/mediaLibrary/MediaLibraryAddModal';
import UploadImage from '../../../common/uploadMedia/v2/UploadImage';
import UploadVideo from '../../../common/uploadMedia/v2/UploadVideo';
import { getAccessDevMode } from '@nexxt/common/firebase/auth/userDetails';

type Props = QuestionFormBaseProps;

interface States extends QuestionFormBaseState {
    isSetupMessage: boolean;
    showEmbeddedLink: boolean;
}

const MediaOptions = [
    { label: 'Image', value: 'Image' },
    { label: 'Video', value: 'Video' }
];

const MediaImageOption = { label: 'Image', value: 'Image' };
const MediaVideoOption = { label: 'Video', value: 'Video' };

class FormMultimedia extends QuestionFormBase<Props, States> {
    constructor(p: QuestionFormBaseProps) {
        super(p, QTYPES.MULTIMEDIA);
        let parentQ: any = {};
        const { isNewQuestion } = this.props;

        if (this.state.question.parent) {
            const questionSummaryObjects = parseQuestionSummaryToObjects(
                this.props.questionSummaries
            );
            parentQ = questionSummaryObjects[this.state.question.parent] || {};
        }
        const question = {
            ...this.state.question,
            showText: !isNewQuestion ? this.state.question.showText : true,
            showContinueButton: !isNewQuestion
                ? this.state.question.showContinueButton
                : true,
            multimediaSettings: isNewQuestion
                ? { showEmbeddedURL: false }
                : this.state.question.multimediaSettings || {}
        };
        this.state = {
            ...this.state,
            mediaType: 'all',
            isSetupMessage:
                parentQ && parentQ.type === QTYPES.POPUP ? true : false,
            question
        };
    }

    /**
     * Toggle media selection
     */
    onToggleMedia = (value) => {
        this.setState(
            {
                question: {
                    ...this.state.question,
                    showImage: value,
                    playVideo: false
                }
            },
            this.setEditFlag
        );
    };

    /**
     * Toggle text selection
     */
    onToggleText = (value) => {
        this.setState(
            (prevState) => ({
                question: {
                    ...prevState.question,
                    showText: value
                }
            }),
            this.setEditFlag
        );
    };

    /**
     * Change question state based on media type
     */
    onMediaTypeChange = (selectedMedia) => {
        if (selectedMedia.value === 'Image') {
            this.setState((prevState, prevProps) => ({
                mediaType: 'image',
                question: {
                    ...prevState.question,
                    showImage: true,
                    playVideo: false
                }
            }));
        } else {
            // Video
            this.setState(
                (prevState, prevProps) => ({
                    mediaType: 'video',
                    question: {
                        ...prevState.question,
                        showImage: false,
                        playVideo: true
                    }
                }),
                this.setEditFlag
            );
        }
        this.setState(
            {
                questionError: {
                    ...this.state.questionError,
                    mediaURL: '',
                    videoURL: ''
                }
            },
            this.setEditFlag
        );
    };

    toggleEmbeddedUrl = (checked: boolean) => {
        this.setState(
            (prevState) => ({
                ...prevState,
                question: {
                    ...prevState.question,
                    multimediaSettings: {
                        showEmbeddedURL: checked
                    }
                }
            }),
            this.setEditFlag
        );
    };
    render() {
        const { currentProject, editEnabled } = this.props;
        const {
            question,
            isSettingBoxLayoutOpen,
            isImageModalOpen,
            templateSettings
        } = this.state;
        let lockMessage;
        const questionError =
            (this.state.showError && this.state.questionError) || {};

        const unlockTemplateQuestion = !editEnabled
            ? !!templateSettings || false
            : true;

        if (templateSettings?.alertMessages?.length) {
            lockMessage = templateSettings?.alertMessages;
        } else {
            lockMessage = this.getWarningMessage();
        }

        const isMedia = question.showImage || question.playVideo || false;
        const MediaSelectedOption = isMedia
            ? question.showImage
                ? MediaImageOption
                : MediaVideoOption
            : null;
        const disableMediaToggle =
            this.state.isSetupMessage ||
            (!templateSettings?.showMedia && !this.props.editEnabled);
        const isDevAllowedAccount = getAccessDevMode();
        const isTemplatedProject = !!currentProject?.templateCode;
        return (
            <React.Fragment>
                {this.state.isMediaLibraryOpen && (
                    <MediaLibraryAddModal
                        isOpen={this.state.isMediaLibraryOpen}
                        setSelectedMedia={this.selectMedia}
                        notify={this.props.notify}
                        onCancel={this.closeMediaLibrary}
                        onClose={this.closeMediaLibrary}
                        type={this.state.mediaType}
                        showMediaLibrary={true}
                        videoOnly={templateSettings?.videoOnly}
                    />
                )}
                {isImageModalOpen && (
                    <ImageModal
                        isOpen={isImageModalOpen}
                        onClose={this.toggleImageModal}
                        imageURL={this.state.currentImage}
                        question={question}
                        cvEnabled={false}
                    />
                )}
                <div>
                    <div className="project-add--question-builder">
                        <BuilderHeader
                            toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                            isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                            showAlert={!this.props.editEnabled}
                            alertMessages={lockMessage}
                            question={question}
                            setValue={this.setQuestion}
                            disabled={!this.props.editEnabled}
                            errorObject={this.state.questionError}
                        />
                        <div className="builder-body">
                            {(isSettingBoxLayoutOpen && (
                                <QuestionSettings
                                    onClose={this.toggleSettingBoxLayout}
                                    question={question}
                                    questionSettings={{
                                        // allowUserEdit: true,
                                        showContinueButton: true,
                                        showDisappearingMedia:
                                            isDevAllowedAccount
                                    }}
                                    setValue={this.setQuestion}
                                    disabled={!this.props.editEnabled}
                                />
                            )) ||
                                null}

                            {!question.showText && !isMedia ? (
                                <Notes note="Please select question text or media type to proceed." />
                            ) : null}
                            {this.renderErrorNotes()}
                            <MultimediaBubble
                                question={question}
                                languages={currentProject.languages}
                                setValue={this.setQuestion}
                                setValueWithLanguage={
                                    this.NEW_setQuestionTextTranslation
                                }
                                addNewBubble={this.NEW_addNewEditor}
                                removeBubble={this.NEW_removeEditor}
                                disabled={!unlockTemplateQuestion}
                                questionError={questionError}
                                showDropdown={
                                    unlockTemplateQuestion &&
                                    templateSettings?.showDropdown
                                }
                                predefinedList={
                                    unlockTemplateQuestion &&
                                    templateSettings?.predefinedList
                                }
                                settings={{
                                    isHTMLText: unlockTemplateQuestion,
                                    showClose: true
                                }}
                            >
                                <Toggle
                                    onChange={this.onToggleText}
                                    checked={question.showText}
                                    className="ml-auto"
                                    disabled={!this.props.editEnabled}
                                />
                            </MultimediaBubble>
                            <div className="option-settings-block">
                                {(isTemplatedProject ||
                                    isDevAllowedAccount) && (
                                    <>
                                        <div className="row">
                                            <div className="col-md-12 form-group">
                                                <div className="form-group-header">
                                                    Show Media:
                                                    <Toggle
                                                        onChange={
                                                            this.onToggleMedia
                                                        }
                                                        checked={
                                                            isMedia || false
                                                        }
                                                        disabled={
                                                            disableMediaToggle
                                                        }
                                                        className="ml-auto"
                                                    />
                                                </div>

                                                {isMedia ? (
                                                    <Select
                                                        className="react-select"
                                                        classNamePrefix="react-select"
                                                        name="form-field-name"
                                                        value={
                                                            MediaSelectedOption
                                                        }
                                                        options={MediaOptions}
                                                        onChange={
                                                            this
                                                                .onMediaTypeChange
                                                        }
                                                        components={{
                                                            IndicatorsContainer
                                                        }}
                                                        isDisabled={
                                                            disableMediaToggle
                                                        }
                                                    />
                                                ) : null}
                                            </div>
                                        </div>
                                        {isMedia && question.showImage ? (
                                            <UploadImage
                                                label="Upload Image"
                                                mediaURL={
                                                    question.mediaURL || ''
                                                }
                                                appyCurrentImage={({
                                                    mediaURL
                                                }) =>
                                                    this.setQuestion(
                                                        'mediaURL',
                                                        mediaURL
                                                    )
                                                }
                                            />
                                        ) : null}
                                        {isMedia && question.playVideo ? (
                                            <UploadVideo
                                                videoURL={question.videoURL}
                                                label="Upload Video"
                                                appyCurrentImage={({
                                                    mediaURL
                                                }) =>
                                                    this.setQuestion(
                                                        'videoURL',
                                                        mediaURL
                                                    )
                                                }
                                                position="full"
                                                disabled={
                                                    !unlockTemplateQuestion
                                                }
                                            />
                                        ) : null}
                                    </>
                                )}
                                <div className="row">
                                    <div className="col-md-12 form-group">
                                        <div className="form-group-header">
                                            Embedded Link:
                                            <Toggle
                                                onChange={
                                                    this.toggleEmbeddedUrl
                                                }
                                                checked={
                                                    question?.multimediaSettings
                                                        ?.showEmbeddedURL
                                                }
                                                disabled={disableMediaToggle}
                                                className="ml-auto"
                                            />
                                        </div>
                                        {question?.multimediaSettings
                                            ?.showEmbeddedURL ? (
                                            <div
                                                className={classnames(
                                                    'col-md-12 form-group',
                                                    {
                                                        error:
                                                            questionError &&
                                                            questionError.embeddedURL
                                                    }
                                                )}
                                            >
                                                <input
                                                    className="form-group__control"
                                                    placeholder=""
                                                    disabled={
                                                        !this.props.editEnabled
                                                    }
                                                    onChange={(
                                                        event: React.ChangeEvent<HTMLInputElement>
                                                    ) =>
                                                        this.setQuestion(
                                                            'embeddedURL',
                                                            event.target.value
                                                        )
                                                    }
                                                    value={question.embeddedURL}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {(question.showContinueButton && (
                                    <MultimediaBubble
                                        languages={currentProject.languages}
                                        question={question}
                                        setValue={this.setQuestion}
                                        setValueWithLanguage={
                                            this.NEW_setQuestionTextTranslation
                                        }
                                        questionError={questionError}
                                        keyName="continueTexts"
                                        disabled={!this.props.editEnabled}
                                        disableAddBubble
                                        defaultTextType={MULTIMEDIA_TYPES.TEXT}
                                        questionText="Continue button text"
                                        defaultText="BUTTON.CONTINUE"
                                        settings={{
                                            noBorder: true,
                                            useTranslation: true
                                        }}
                                    />
                                )) ||
                                    null}
                            </div>
                        </div>
                        {/*end of builder body*/}
                    </div>
                </div>
            </React.Fragment>
        );
    }
}

export default withRouter(
    NotifyHOC(
        connect(
            mapStateToQuestionFormBaseProps,
            mapDispatchToQuestionFormBaseProps
        )(FormMultimedia)
    )
);
